/**
 * @fileoverview Contact-people linking inside the contact Links section.
 *
 * Links individual (person) job contacts to a company (organisation) job
 * contact and designates which linked person is the company's primary
 * contact. The primary person's name/email/phone flow into document
 * generation via the `contact_job_primary_contact_*` fields.
 *
 * Two shapes depending on the open contact:
 * - Company contact: manage the list of linked people, pick the primary,
 *   and link additional people.
 * - Person contact: show which company it is a contact person for (read-
 *   only chip) with an unlink action, or link it to a company.
 */

import { useState } from "react";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Building2, Star, UserRound, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/ui/primitives/button";
import { SearchableSelect } from "@/ui/composed/inputs/searchable-select";
import { extractErrorMessage } from "@/utils/errors";
import type { JobContactRow } from "./types";

interface ContactPeopleSectionProps {
  contact: JobContactRow;
  /** Other rows on this job (for the link pickers and linked-people list). */
  jobContacts: Array<JobContactRow>;
  disabled?: boolean;
}

export function ContactPeopleSection({
  contact,
  jobContacts,
  disabled,
}: ContactPeopleSectionProps) {
  const linkToCompany = useConvexMutation(
    api.contacts.mutations.linkJobContactToCompany,
  );
  const unlinkFromCompany = useConvexMutation(
    api.contacts.mutations.unlinkJobContactFromCompany,
  );
  const setPrimary = useConvexMutation(
    api.contacts.mutations.setCompanyPrimaryContactPerson,
  );

  const [selectedContactId, setSelectedContactId] = useState<string>("");

  const run = async (op: Promise<unknown>, success: string) => {
    try {
      await op;
      toast.success(success);
      setSelectedContactId("");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Something went wrong"));
    }
  };

  const isPerson = contact.contactType === "person";

  // ---------------------------------------------------------------------
  // Person contact — "Contact for <company>"
  // ---------------------------------------------------------------------
  if (isPerson) {
    const companyOptions = jobContacts
      .filter((c) => c.contactType !== "person" && c.status === "active")
      .map((c) => ({ value: c._id, label: c.displayName }));

    return (
      <div className="flex flex-col gap-2">
        <h4 className="font-sans text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Contact person for
        </h4>
        {contact.linkedCompanyContactId ? (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/20 p-3">
            <div className="flex min-w-0 items-center gap-2">
              <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate text-sm font-medium text-foreground">
                {contact.linkedCompanyName ?? "Linked company"}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 shrink-0 text-xs"
              disabled={disabled}
              onClick={() =>
                run(
                  unlinkFromCompany({ personContactId: contact._id }),
                  "Removed the company link",
                )
              }
            >
              <X className="mr-1 h-3 w-3" />
              Unlink
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-2 rounded-lg border border-dashed border-border bg-muted/10 p-3">
            <p className="text-xs text-muted-foreground">
              Not linked to a company. Link this person as a contact person so
              their name can be used in documents addressed to the company.
            </p>
            {companyOptions.length > 0 ? (
              <div className="flex gap-2">
                <SearchableSelect
                  value={selectedContactId}
                  onValueChange={setSelectedContactId}
                  options={companyOptions}
                  placeholder="Choose a company contact…"
                  searchPlaceholder="Search companies…"
                />
                <Button
                  size="sm"
                  className="h-8 shrink-0 text-xs"
                  disabled={disabled || !selectedContactId}
                  onClick={() =>
                    run(
                      linkToCompany({
                        personContactId: contact._id,
                        companyContactId:
                          selectedContactId as Id<"jobContacts">,
                      }),
                      "Linked to the company",
                    )
                  }
                >
                  Link
                </Button>
              </div>
            ) : (
              <p className="text-[11px] text-muted-foreground/80">
                No company contacts on this job yet.
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------
  // Company contact — manage linked people + primary
  // ---------------------------------------------------------------------
  const linkedPeople = jobContacts.filter(
    (c) => c.linkedCompanyContactId === contact._id && c.status !== "merged",
  );
  const linkSelectedPerson = () => {
    if (!selectedContactId) return;
    void run(
      linkToCompany({
        personContactId: selectedContactId as Id<"jobContacts">,
        companyContactId: contact._id,
      }),
      "Linked the contact person",
    );
  };

  const linkedIds = new Set(linkedPeople.map((c) => c._id));
  const personOptions = jobContacts
    .filter(
      (c) =>
        c.contactType === "person" &&
        c.status === "active" &&
        !linkedIds.has(c._id),
    )
    .map((c) => ({
      value: c._id,
      label: c.displayName,
      title: c.linkedCompanyName
        ? `${c.displayName} — currently linked to ${c.linkedCompanyName}`
        : undefined,
    }));

  return (
    <div className="flex flex-col gap-2">
      <h4 className="font-sans text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        People contacts
      </h4>

      {linkedPeople.length > 0 ? (
        <ul className="flex flex-col gap-1.5">
          {linkedPeople.map((person) => {
            const isPrimary = contact.primaryContactPersonId === person._id;
            return (
              <li
                key={person._id}
                className="flex items-center justify-between gap-2 rounded-lg border border-border bg-muted/20 p-2.5"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <UserRound className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate text-sm font-medium text-foreground">
                    {person.displayName}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 text-xs"
                    disabled={disabled}
                    aria-label={
                      isPrimary
                        ? "Clear primary contact"
                        : "Set as primary contact"
                    }
                    onClick={() =>
                      run(
                        setPrimary({
                          companyContactId: contact._id,
                          personContactId: isPrimary ? null : person._id,
                        }),
                        isPrimary
                          ? "Cleared the primary contact"
                          : `Set ${person.displayName} as primary contact`,
                      )
                    }
                  >
                    <Star
                      className={
                        isPrimary
                          ? "h-3.5 w-3.5 fill-amber-400 text-amber-400"
                          : "h-3.5 w-3.5 text-muted-foreground/40"
                      }
                    />
                    {isPrimary ? "Primary" : "Make primary"}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={disabled}
                    onClick={() =>
                      run(
                        unlinkFromCompany({ personContactId: person._id }),
                        `Unlinked ${person.displayName}`,
                      )
                    }
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-border bg-muted/10 p-3">
          <p className="text-xs text-muted-foreground">
            None linked yet. Link people so that you can use their details in
            documents generated for this company.
          </p>
          {personOptions.length > 0 ? (
            <div className="flex shrink-0 gap-2">
              <SearchableSelect
                value={selectedContactId}
                onValueChange={setSelectedContactId}
                options={personOptions}
                placeholder="Link a person contact…"
                searchPlaceholder="Search people…"
              />
              <Button
                size="sm"
                className="h-8 shrink-0 text-xs"
                disabled={disabled || !selectedContactId}
                onClick={linkSelectedPerson}
              >
                Link
              </Button>
            </div>
          ) : (
            <p className="shrink-0 text-[11px] text-muted-foreground/80">
              No person contacts on this job yet.
            </p>
          )}
        </div>
      )}

      {linkedPeople.length > 0 && personOptions.length > 0 && (
        <div className="flex gap-2">
          <SearchableSelect
            value={selectedContactId}
            onValueChange={setSelectedContactId}
            options={personOptions}
            placeholder="Link a person contact…"
            searchPlaceholder="Search people…"
          />
          <Button
            size="sm"
            className="h-8 shrink-0 text-xs"
            disabled={disabled || !selectedContactId}
            onClick={linkSelectedPerson}
          >
            Link
          </Button>
        </div>
      )}
      {linkedPeople.length > 0 && (
        <p className="text-[11px] text-muted-foreground/80">
          The primary contact person&rsquo;s name is available in document
          generation via the &ldquo;Primary Contact&rdquo; fields.
        </p>
      )}
    </div>
  );
}
